import { FC, useMemo, useEffect } from "react";
import { Briefcase, CheckCircle, Clock, Users, Package, AlertCircle, FileText } from "lucide-react";
import { useMissionControlStore } from "../../mission-control.store";
import { useGetBookings } from "~/services/booking";
import { JobStatus } from "~/utils/constants";

type Props = {};

export const StatsDashboard: FC<Props> = () => {
  const { selectedProjectId, selectedProject, setLoading } = useMissionControlStore();

  // Fetch bookings for the selected project
  const { data: bookingsData, isLoading } = useGetBookings(
    selectedProjectId
      ? {
          projectId: selectedProjectId,
          pageSize: 500,
        }
      : undefined
  );

  // Sync loading state with store
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  const stats = useMemo(() => {
    if (!selectedProjectId || !bookingsData?.items) {
      return {
        totalBookings: 0,
        totalServiceTickets: 0,
        completedTickets: 0,
        inProgressTickets: 0,
        scheduledTickets: 0,
        estimatedTickets: 0,
        actionableTickets: 0,
        uniqueStaff: 0,
        totalInventory: 0,
        uniqueClients: 0,
      };
    }

    const bookings = bookingsData.items;
    const allServiceTickets = bookings.flatMap((b) => b.serviceTicket || []);
    const allStaffIds = new Set<string>();
    const allClientIds = new Set<string>();
    let totalInventory = 0;

    allServiceTickets.forEach((ticket) => {
      // Count unique staff
      ticket.staffs?.forEach((staff: any) => {
        allStaffIds.add(staff.id);
      });

      // Count inventory
      totalInventory += ticket.inventories?.length || 0;
    });

    // Count unique clients from bookings
    bookings.forEach((booking) => {
      // Try both client object and clientId
      if (booking.client?.id) {
        allClientIds.add(booking.client.id);
      } else if (booking.clientId) {
        allClientIds.add(booking.clientId);
      }
    });

    // Count service tickets by status using JobStatus enum
    const completedTickets = allServiceTickets.filter(
      (t) => t.status === JobStatus.COMPLETED
    ).length;
    const inProgressTickets = allServiceTickets.filter(
      (t) => t.status === JobStatus.IN_PROGRESS
    ).length;
    const scheduledTickets = allServiceTickets.filter(
      (t) => t.status === JobStatus.SCHEDULED
    ).length;
    const estimatedTickets = allServiceTickets.filter(
      (t) => t.status === JobStatus.ESTIMATED
    ).length;

    // Calculate completion rate (exclude estimates and drafts from denominator)
    const actionableTickets = allServiceTickets.filter(
      (t) =>
        t.status !== JobStatus.ESTIMATED &&
        t.status !== JobStatus.DRAFT &&
        t.status !== JobStatus.CANCELLED
    ).length;

    return {
      totalBookings: bookings.length,
      totalServiceTickets: allServiceTickets.length,
      completedTickets,
      inProgressTickets,
      scheduledTickets,
      estimatedTickets,
      actionableTickets,
      uniqueStaff: allStaffIds.size,
      totalInventory,
      uniqueClients: allClientIds.size,
    };
  }, [selectedProjectId, bookingsData]);

  if (!selectedProjectId) {
    return (
      <div className="bg-gray-50 border-2 border-dashed rounded-lg p-12 text-center">
        <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-lg font-medium text-gray-600 mb-2">
          No Project Selected
        </h3>
        <p className="text-gray-500">
          Select a project above to view mission control statistics
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Project Statistics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Bookings */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Bookings</span>
            <Briefcase className="text-blue-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {stats.totalBookings}
          </p>
        </div>

        {/* Service Tickets */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Service Tickets
            </span>
            <CheckCircle className="text-green-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-green-600">
            {stats.totalServiceTickets}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
            <span className="text-green-600">
              ✓ {stats.completedTickets} completed
            </span>
            <span className="text-blue-600">
              ⏵ {stats.inProgressTickets} in progress
            </span>
            <span className="text-gray-600">
              ○ {stats.scheduledTickets} scheduled
            </span>
            <span className="text-amber-600">
              ◈ {stats.estimatedTickets} estimates
            </span>
          </div>
        </div>

        {/* Staff */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Active Staff
            </span>
            <Users className="text-purple-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {stats.uniqueStaff}
          </p>
        </div>

        {/* Inventory */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Total Inventory
            </span>
            <Package className="text-orange-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-orange-600">
            {stats.totalInventory}
          </p>
        </div>

        {/* Clients */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Clients</span>
            <Users className="text-teal-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-teal-600">
            {stats.uniqueClients}
          </p>
        </div>

        {/* Completion Rate */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Completion Rate
            </span>
            <Clock className="text-indigo-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-indigo-600">
            {stats.actionableTickets > 0
              ? Math.round(
                  (stats.completedTickets / stats.actionableTickets) * 100
                )
              : 0}
            %
          </p>
          <div className="mt-2 text-xs text-gray-500">
            {stats.completedTickets} of {stats.actionableTickets} completed
          </div>
        </div>
      </div>
    </div>
  );
};
